import React from 'react';
import type { ProjectContextLevel } from '@/components/Inputs/ProjectContextOption';
import {
  BusinessUnitOption,
  NestedOptions,
  useAllProjectOptions,
  useBusinessUnits,
} from '@/hooks/useProjectOptions';

/** A context the user is trying to land on: company alone, a business unit
 * inside it, or a project (whose business unit is implied). */
export type ContextToReach = {
  company_id: number;
  business_unit_id?: number | null;
  project_id?: number | null;
};

export const contextLevel = (context: ContextToReach): ProjectContextLevel =>
  context.project_id
    ? 'project'
    : context.business_unit_id
      ? 'business-unit'
      : 'company';

/**
 * Thrown when a context exists (the slugs resolve) but none of the user's
 * roles reaches it: a project-only user landing on the company-wide context
 * or on a business unit, a business-unit user landing on the whole company,
 * or a deep link to a sibling project (BIL-5009).
 */
export class ContextOutOfReachError extends Error {
  constructor(
    public readonly level: ProjectContextLevel,
    context_name: string
  ) {
    super(context_name);
    this.name = 'ContextOutOfReachError';
    this.message = `${context_name} (${level}) is not reached by any of the current logged in user's roles.`;
  }
}

/**
 * Whether the user's roles reach `context` as a selectable context. This is
 * the one rule behind every surface that offers or resolves a context: the
 * project switcher, the company chooser and the URL. The options already
 * encode the reach of each role (BIL-4887): a company-level role adds the
 * "all projects" entry, a business-unit role marks its unit selectable, and a
 * project role only adds its own project.
 */
export const isContextReachable = (
  companyOptions: readonly NestedOptions[],
  businessUnits: readonly BusinessUnitOption[],
  context: ContextToReach
): boolean => {
  const company = companyOptions.find(
    (option) => option.id === context.company_id
  );
  if (!company) return false;
  if (context.project_id) {
    const project_id = context.project_id;
    return company.options.some(
      (option) => option.value.project_id === project_id
    );
  }
  if (context.business_unit_id) {
    const business_unit_id = context.business_unit_id;
    return businessUnits.some(
      (businessUnit) =>
        businessUnit.id === business_unit_id &&
        businessUnit.company_id === context.company_id &&
        businessUnit.selectable
    );
  }
  return company.options.some(
    (option) => !option.value.project_id && !option.value.business_unit_id
  );
};

export const useIsContextReachable = () => {
  const companyOptions = useAllProjectOptions();
  const businessUnits = useBusinessUnits();
  return React.useCallback(
    (context: ContextToReach) =>
      isContextReachable(companyOptions, businessUnits, context),
    [companyOptions, businessUnits]
  );
};
